import { Badge, HStack, Text, TextProps } from "@chakra-ui/react"
// import { PathiconProps } from "@pathwright/app-web/src/components/pathicon/ExpensivePathicon"
import { IconName } from "@pathwright/pathicons"
import Pathicon from "@pathwright/web/src/modules/pathicon/Pathicon"
import { RegistrationRoleEnum } from "../../api/generated"
import { SelectedCohortRole, SelectedResourceRole } from "./state"

const roleOptionMap = getRoleOptionMap()

const observerMap = {
  enum: RegistrationRoleEnum.Observer,
  int: -1,
  options: []
}

const learnerMap = {
  enum: RegistrationRoleEnum.Student,
  int: 5,
  options: [roleOptionMap.learn]
}

const moderatorMap = {
  enum: RegistrationRoleEnum.Moderator,
  int: 10,
  options: [roleOptionMap.teach, roleOptionMap.learn]
}

const teacherMap = {
  enum: RegistrationRoleEnum.Teacher,
  int: 15,
  // Is it possible Teach role maps to either [roleOptionMap.teach] or [roleOptionMap.design, roleOptionMap.teach]?
  // This consideration applies to contexts where the user CANNOT choose to be a Source Cohort designer, in which
  // case the Design role simply applies to the Cohort and they would become a Teacher in that case (and selecting
  // Teach role would have no change).
  options: [roleOptionMap.teach]
}

const editorMap = {
  enum: RegistrationRoleEnum.Editor,
  int: 20,
  options: [roleOptionMap.design]
}

const registrationRoleMaps = [
  observerMap,
  learnerMap,
  moderatorMap,
  teacherMap,
  editorMap
]

type RegistrationRoleMap = (typeof registrationRoleMaps)[number]

// Map registration role ints to role maps.
export const registrationRoleIntMap = new Map(
  registrationRoleMaps.reduce((acc, roleMap) => {
    acc.push([roleMap.int, roleMap])
    return acc
  }, [] as [number, RegistrationRoleMap][])
)

// Map registration role enums to role maps.
export const registrationRoleEnumMap = new Map(
  registrationRoleMaps.reduce((acc, roleMap) => {
    acc.push([roleMap.enum, roleMap])
    return acc
  }, [] as [RegistrationRoleEnum, RegistrationRoleMap][])
)

type Badge = {
  icon?: IconName
  label: string
  color: string
}

export type RoleOptionValue = "design" | "teach" | "learn"

export interface RoleOption {
  badge: Badge
  description: string
  value: RoleOptionValue
}

type RoleOptionMap = Record<RoleOptionValue, RoleOption>

export function getRoleOptions(): RoleOption[] {
  // Defining with fn in case we need to translate the lagels (which we will).
  const roleOptions: RoleOption[] = [
    {
      badge: {
        icon: "edit",
        label: "Design",
        color: "blue"
      },
      description: "Design Path & content for all Cohorts",
      value: "design"
    },
    {
      badge: {
        icon: "group",
        label: "Teach",
        color: "green"
      },
      description: "Invite, schedule, & give feedback",
      value: "teach"
    },
    {
      badge: {
        icon: "avatar-square",
        label: "Learn",
        color: "orange"
      },
      description: "Track your progress",
      value: "learn"
    }
  ]

  return roleOptions
}

export function getRoleOptionMap(): RoleOptionMap {
  const roleOptions = getRoleOptions()
  const roleOptionMap = roleOptions.reduce((acc, roleOption) => {
    acc[roleOption.value] = roleOption
    return acc
  }, {} as RoleOptionMap)

  return roleOptionMap
}

export const pathRoles: SelectedResourceRole[] = [RegistrationRoleEnum.Editor]
export const cohortRoles: SelectedCohortRole[] = [
  RegistrationRoleEnum.Student,
  RegistrationRoleEnum.Moderator,
  RegistrationRoleEnum.Teacher
]

export function serializeRoleBadgesToRegistrationRoles(
  roleOptions: RoleOption[]
) {
  function getRole<T extends SelectedResourceRole | SelectedCohortRole>(
    roles: T[]
  ): T | null {
    // Get the number of roles from the related role map that are found in
    // the selected options.
    function getSize(registrationRoleMap: RegistrationRoleMap) {
      return registrationRoleMap.options.filter((option) =>
        roleOptions.some((roleOption) => option.value === roleOption.value)
      ).length
    }

    const roleMap = registrationRoleMaps
      .filter(
        (registrationRoleMap) =>
          //Require role maps that to have options (Observer).
          registrationRoleMap.options.length &&
          // Require the role map to be scoped ot the provided roles.
          roles.includes(registrationRoleMap.enum as T) &&
          // Require every role map option to be found in the provided role options.
          getSize(registrationRoleMap) === registrationRoleMap.options.length
      )
      // Now we sort based on the number of matching role options, selecting the role
      // map with the highest count.
      .sort((roleMapA, roleMapB) => {
        return getSize(roleMapB) - getSize(roleMapA)
      })[0]

    return (roleMap?.enum as T) || null
  }

  return {
    resourceRole: getRole(pathRoles),
    cohortRole: getRole(cohortRoles)
  }
}

export function sortRoleOptionsByRole(roleOptions: RoleOption[]) {
  const roleValues = getRoleOptions().map((option) => option.value)
  // Sort higest roles to front for consistent ordering.
  const sortedRoleOptions = roleOptions.sort((optionA, optionB) => {
    function getIndex(option: RoleOption) {
      return roleValues.findIndex((role) => option.value === role)
    }
    // Sort null values to the end.
    const valueA = getIndex(optionA)
    const valueB = getIndex(optionB)
    return valueA - valueB
  })

  return sortedRoleOptions
}

function RoleBadge({
  badge,
  iconOnly,
  highlight,
  ...props
}: { badge: Badge; iconOnly?: boolean; highlight?: boolean } & TextProps) {
  const boxStyles = highlight
    ? {
        bg: `${badge.color}.500`,
        color: "white",
        shadow: "md"
      }
    : {
        _hover: {
          bg: `${badge.color}.200`
        }
      }

  const textStyles = highlight
    ? {
        color: `${badge.color}.100`
      }
    : {
        color: `${badge.color}.500`
      }

  return (
    <HStack
      size="xs"
      fontWeight="bold"
      borderRadius="10px"
      bg="rgba(255,255,255,.8)"
      m={0}
      my={1}
      px={2}
      py={1}
      shadow="md"
      {...boxStyles}
      {...props}
    >
      {!!badge.icon && (
        <Pathicon icon={badge.icon} size=".8em" {...textStyles} />
      )}
      {!iconOnly && (
        <Text as="span" {...textStyles}>
          {badge.label}
        </Text>
      )}
    </HStack>
  )
}

export default RoleBadge
